import { action, makeAutoObservable, observable } from 'mobx';

export const apiResponse: IAPICustomerFacingPaymentModel = {
    feedbackData: {
        header: 'Absolute Pilates Upstairs',
        description:
            '“I was speaking at a convention with 16,000 attendees and had people signing up while I was talking...game changer.”',
        url: 'https://www.thestatesman.com/wp-content/uploads/2019/05/Women-entrepreneurs.jpg',
    },
    summaryData: {},
    codes: [{ code: 'PRAS12', discount: 25 }],
    items: [
        { title: 'Due Now', content: '$2,939.74', expanded: false },
        { title: 'Due Every 2 Months', content: '$92.70 starting Dec 24, 2023', expanded: false },
        { title: 'Due Every Year', content: '$257.50 starting Nov 24, 2024', expanded: false },
    ],
};

export class AccountDetails {
    @observable name: string;
    @observable accountType: string;

    constructor(data?: Partial<AccountDetails>) {
        makeAutoObservable(this);
        this.name = data?.name || '';
        this.accountType = data?.accountType || '';
    }

    @action
    updateName = (name: string) => {
        this.name = name;
    };

    @action
    updateAccountType = (type: string) => {
        this.accountType = type;
    };
}

export class CustomerFacingPaymentModel {
    @observable summaryData: SummaryModel;
    @observable feedbackData: FeedbackModel;
    @observable inputData: InputModel;
    @observable updates: boolean = false;
    @observable savePayment: boolean = false;
    @observable terms: boolean = false;
    @observable isPromotionClicked: boolean = false;
    @observable promotionCode: string = '';
    @observable codes: ICodesModel[];
    @observable promoText: boolean | null = null;
    @observable items: IItemsModel[];
    @observable paymentMethod: string;
    @observable accountDetails: AccountDetails;

    constructor(data?: Partial<CustomerFacingPaymentModel>) {
        makeAutoObservable(this);
        this.summaryData = data?.summaryData || new SummaryModel();
        this.feedbackData = data?.feedbackData || new FeedbackModel();
        this.inputData = data?.inputData || new InputModel();
        this.codes = data?.codes || [];
        this.items = data?.items || [];
        this.paymentMethod = data?.paymentMethod || 'Card';
        this.accountDetails = data?.accountDetails || new AccountDetails();
    }

    static deserialize(data: IAPICustomerFacingPaymentModel): CustomerFacingPaymentModel {
        return new CustomerFacingPaymentModel({
            summaryData: new SummaryModel(),
            feedbackData: new FeedbackModel(data.feedbackData),
            codes: data.codes,
            items: data.items,
        });
    }

    @action
    setPaymentMethod(type: string) {
        this.paymentMethod = type;
    }

    @action
    toggleExpand = (title: string) => {
        const selectedItemndex = this.items.findIndex((item) => item.title === title);
        this.items[selectedItemndex].expanded = !this.items[selectedItemndex].expanded;
        this.updateItems(this.items);
    };

    @action
    updateItems = (items: IItemsModel[]) => {
        this.items = items;
    };

    @action
    updateCode = (code: string) => {
        this.promotionCode = code;
    };

    @action
    updateInputData = (data: InputModel) => {
        this.inputData = data;
    };

    @action
    setUpdates(value: boolean) {
        this.updates = value;
    }

    @action
    setSavePayment(value: boolean) {
        this.savePayment = value;
    }

    @action
    togglePromotion = () => {
        this.isPromotionClicked = !this.isPromotionClicked;
    };

    @action
    checkPromoCode = (): void => {
        const isValid = this.codes.some((item: ICodesModel) => item.code === this.promotionCode);
        if (isValid) {
            this.promoText = true;
        } else {
            this.promoText = false;
        }
    };

    @action
    setTerms(value: boolean) {
        this.terms = value;
    }

    @action
    areAllChecked(): boolean {
        return this.updates && this.savePayment && this.terms;
    }
}

export class SummaryModel {}

export class FeedbackModel {
    header: string;
    description: string;
    url: string;

    constructor(data?: Partial<FeedbackModel>) {
        this.header = data?.header || '';
        this.description = data?.description ?? '';
        this.url = data?.url || '';
    }
}

export class InputModel {
    @observable public firstName: string;
    @observable public lastName: string;
    @observable public email: string;
    constructor(data?: Partial<InputModel>) {
        this.firstName = data?.firstName || '';
        this.lastName = data?.lastName || '';
        this.email = data?.email || '';
    }
}

export interface IAPICustomerFacingPaymentModel {
    summaryData: SummaryModel;
    feedbackData: FeedbackModel;
    codes: ICodesModel[];
    items: IItemsModel[];
}

export interface IItemsModel {
    title: string;
    content: string;
    expanded: boolean;
}

export interface ICodesModel {
    code: string;
    discount: number;
}

export interface IProduct {
    id: number;
    name: string;
    price: number;
    discountPrice?: number;
    color?: string;
    size?: string;
    quantity: number;
    isFree?: boolean;
    url: string;
}

export interface IService {
    id: number;
    name: string;
    price: number;
    renewalTerms: string;
    renewalPrice: number;
    quantity: number;
    discountPrice?: number;
    url: string;
}

export interface ICart {
    products: IProduct[];
    services: IService[];
}
